import { type FC, useEffect, useState, useRef } from 'react';
import { createPaginationContainer, graphql, type RelayPaginationProp } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers.jsx';
import { getBuyerId } from 'dibs-cookie-jar';
import { trackEvent } from 'dibs-tracking';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { useEtherConversion } from 'dibs-crypto-utils/exports/useEtherConversion.ts';
import { NftPdpHistoryItem } from './NftPdpHistoryItem/NftPdpHistoryItem.tsx';
import classnames from 'classnames';

import { type NftPdpHistory_nftItem$data } from './__generated__/NftPdpHistory_nftItem.graphql.ts';
import { type NftPdpHistory_viewer$data } from './__generated__/NftPdpHistory_viewer.graphql.ts';

import styles from './NftPdpHistory.scss';

type Props = {
    nftItem: NftPdpHistory_nftItem$data | null | undefined;
    viewer: NftPdpHistory_viewer$data | null | undefined;
    relay: RelayPaginationProp;
    hasEditions: boolean;
};

const NftPdpHistoryComponent: FC<Props> = ({ nftItem, viewer, relay, hasEditions }) => {
    const sectionRef = useRef<HTMLDivElement | null>(null);

    const { nftItemHistory, user } = viewer || {};
    const { etherRates, tokenId } = nftItem || {};
    // console.log(tokenId);

    const { currency } = useCurrency();
    const [isRefetching, setIsRefetching] = useState(false);
    const { getAmountStrings } = useEtherConversion({
        currency,
        etherRates: etherRates ? etherRates.filter(filterNulls) : null,
    });

    const REFETCH_COUNT = 6;

    useEffect(() => {
        const buyerId = getBuyerId(document.cookie);
        if (buyerId) {
            relay.refetchConnection(REFETCH_COUNT, () => {}, {
                userId: buyerId,
                fetchUser: true,
            });
        }
        addLoginHandlers(() => {
            const userId = getBuyerId(document.cookie);
            if (userId) {
                relay.refetchConnection(REFETCH_COUNT, () => {}, {
                    userId,
                    fetchUser: true,
                });
            }
        });
    }, [relay]);

    const events = nftItemHistory?.events?.edges?.map(edge => edge?.node).filter(filterFalsy);

    if (!events?.length) {
        return null;
    }

    return (
        <>
            <VisibilityTracker
                elementRef={sectionRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackEvent({
                            category: 'product',
                            action: 'below the fold reached',
                            label: 'PDP',
                        });
                    }
                }}
            />
            <div
                className={classnames(styles.container, {
                    [styles.hasEditions]: hasEditions,
                })}
                ref={sectionRef}
            >
                {!hasEditions && (
                    <HeadingLevel>
                        {Header => (
                            <Header className={styles.header}>
                                <FormattedMessage
                                    id="nft.pdp.history.header"
                                    defaultMessage="History"
                                />
                            </Header>
                        )}
                    </HeadingLevel>
                )}

                <div className={styles.eventListContainer}>
                    <ul className={styles.eventList}>
                        {events.map(event => {
                            return (
                                <NftPdpHistoryItem
                                    key={event.id}
                                    nftItemHistoryEvent={event}
                                    getAmountStrings={getAmountStrings}
                                    userWalletAddress={user?.ethereumWalletAddress?.address}
                                    tokenId={tokenId || ''}
                                />
                            );
                        })}
                    </ul>
                    {relay.hasMore() && !isRefetching && (
                        <Link
                            className={styles.loadMoreButton}
                            onClick={() => {
                                setIsRefetching(true);
                                relay.loadMore(5, () => {
                                    setIsRefetching(false);
                                });
                            }}
                        >
                            <FormattedMessage
                                id="nft.pdp.history.viewMore"
                                defaultMessage="View More"
                            />
                        </Link>
                    )}
                </div>
            </div>
        </>
    );
};

// NftPdpHistory_nftItemHistory fragment is used in subscription
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fragment = graphql`
    fragment NftPdpHistory_nftItemHistory on NftItemHistory
    @argumentDefinitions(itemCount: { type: "Int", defaultValue: 6 }, after: { type: "String" }) {
        events(first: $itemCount, after: $after) @connection(key: "NftPdpHistory_events") {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                node {
                    ...NftPdpHistoryItem_nftItemHistoryEvent
                }
            }
        }
    }
`;

export const NftPdpHistory = createPaginationContainer(
    NftPdpHistoryComponent,
    {
        viewer: graphql`
            fragment NftPdpHistory_viewer on Viewer
            @argumentDefinitions(
                itemCount: { type: "Int", defaultValue: 6 }
                after: { type: "String" }
                userId: { type: "String", defaultValue: "" }
                fetchUser: { type: "Boolean", defaultValue: false }
            ) {
                nftItemHistory(itemId: $nftId) {
                    events(first: $itemCount, after: $after)
                        @connection(key: "NftPdpHistory_events") {
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                        edges {
                            node {
                                __typename
                                timestamp
                                id
                                ...NftPdpHistoryItem_nftItemHistoryEvent
                            }
                        }
                    }
                }
                user(userId: $userId) @include(if: $fetchUser) {
                    ethereumWalletAddress {
                        address
                    }
                }
            }
        `,
        nftItem: graphql`
            fragment NftPdpHistory_nftItem on NftItem {
                serviceId
                tokenId
                etherRates {
                    ...useEtherConversion_etherRates
                }
            }
        `,
    },
    {
        direction: 'forward',
        getConnectionFromProps(props) {
            return props.viewer?.nftItemHistory?.events || null;
        },
        getVariables(props, { count, cursor }) {
            return {
                itemCount: count,
                after: cursor,
                nftId: props?.nftItem?.serviceId || '',
            };
        },
        query: graphql`
            query NftPdpHistoryEventsRefetchQuery(
                $nftId: String!
                $after: String
                $fetchUser: Boolean = false
                $userId: String = ""
            ) {
                viewer {
                    ...NftPdpHistory_viewer
                        @arguments(after: $after, fetchUser: $fetchUser, userId: $userId)
                }
            }
        `,
    }
);
